import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { type Path, useFieldArray, useForm } from "react-hook-form";
import { Card, Form, useToast } from "@/components/ui";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RepairIntakeFormActions } from "@/pages/public/repairIntakeForm/RepairIntakeFormActions";
import { RepairIntakeStepFields } from "@/pages/public/repairIntakeForm/RepairIntakeStepFields";
import { RepairIntakeStepNavigation } from "@/pages/public/repairIntakeForm/RepairIntakeStepNavigation";
import { RepairIntakeStepProgress } from "@/pages/public/repairIntakeForm/RepairIntakeStepProgress";
import { RepairIntakeSuccessState } from "@/pages/public/repairIntakeForm/RepairIntakeSuccessState";
import {
  createDefectLengthEntry,
  createDesiredRepairLengthEntry,
  createEmptyComponentEntry,
  getStepNumber,
  type SectionKey,
  stepDefinitions,
  withComponentLabels,
} from "@/pages/public/repairIntakeForm/repairIntakeFormConstants";
import { repairIntakeSchema } from "@/pages/public/repairIntakeForm/repairIntakeFormSchema";
import useRepairIntakeStore, { type RepairIntakeFormData } from "@/store/useRepairIntakeStore";

function RepairIntakeForm() {
  const { toast } = useToast();
  const { formData, submitted, setSection, resetForm, submitForm } = useRepairIntakeStore();
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccessState, setShowSuccessState] = useState(submitted);

  const form = useForm<RepairIntakeFormData>({
    resolver: zodResolver(repairIntakeSchema),
    mode: "onChange",
    defaultValues: formData,
  });

  const componentEntriesArray = useFieldArray({
    control: form.control,
    name: "section1.componentEntries",
  });
  const defectLengthArray = useFieldArray({
    control: form.control,
    name: "section5.defectLengthEntries",
  });
  const desiredRepairLengthArray = useFieldArray({
    control: form.control,
    name: "section5.desiredRepairLengthEntries",
  });

  const currentStep = stepDefinitions[activeStep];
  const componentTypes = form.watch("section1.componentTypes");
  const repairTypes = form.watch("section3.repairTypes");
  const defectTypes = form.watch("section5.defectTypes");
  const utReadingsAttached = form.watch("section2.utReadingsAttached");
  const activeLeak = form.watch("section6.activeLeak");
  const desiredRepairLife = form.watch("section7.desiredRepairLife");
  const goodPipeOnLandingZones = form.watch("section7.goodPipeOnLandingZones");
  const attachments = form.watch("section9.attachments");
  const componentEntries = form.watch("section1.componentEntries");

  useEffect(() => {
    document.title = "Repair Intake Form";

    const existingMeta = document.querySelector('meta[name="robots"]');
    const previousContent = existingMeta?.getAttribute("content") ?? null;
    const meta = existingMeta ?? document.createElement("meta");

    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow");

    if (!existingMeta) {
      document.head.appendChild(meta);
    }

    return () => {
      if (previousContent) {
        meta.setAttribute("content", previousContent);
      } else if (!existingMeta) {
        meta.remove();
      }
    };
  }, []);

  useEffect(() => {
    setShowSuccessState(submitted);
  }, [submitted]);

  useEffect(() => {
    const desiredCount = componentEntries.length || 1;
    const currentDefectEntries = form.getValues("section5.defectLengthEntries");
    const currentDesiredRepairEntries = form.getValues("section5.desiredRepairLengthEntries");

    if (currentDefectEntries.length !== desiredCount) {
      defectLengthArray.replace(
        Array.from({ length: desiredCount }, (_, index) => createDefectLengthEntry(index, currentDefectEntries[index]))
      );
    } else {
      currentDefectEntries.forEach((entry, index) => {
        const expectedLabel = `Component ${index + 1}`;
        if (entry.componentLabel !== expectedLabel) {
          form.setValue(`section5.defectLengthEntries.${index}.componentLabel`, expectedLabel, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
      });
    }

    if (currentDesiredRepairEntries.length !== desiredCount) {
      desiredRepairLengthArray.replace(
        Array.from({ length: desiredCount }, (_, index) =>
          createDesiredRepairLengthEntry(index, currentDesiredRepairEntries[index])
        )
      );
    } else {
      currentDesiredRepairEntries.forEach((entry, index) => {
        const expectedLabel = `Component ${index + 1}`;
        if (entry.componentLabel !== expectedLabel) {
          form.setValue(`section5.desiredRepairLengthEntries.${index}.componentLabel`, expectedLabel, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
      });
    }
  }, [componentEntries.length, defectLengthArray, desiredRepairLengthArray, form]);

  useEffect(() => {
    const subscription = form.watch((values, meta) => {
      const sectionKey = meta.name?.split(".")[0] as SectionKey | undefined;
      if (!sectionKey) {
        return;
      }

      const sectionValue = values[sectionKey];
      if (!sectionValue) {
        return;
      }

      const normalizedValues = withComponentLabels(values as RepairIntakeFormData);
      setSection(getStepNumber(sectionKey), normalizedValues[sectionKey]);
    });

    return () => subscription.unsubscribe();
  }, [form, setSection]);

  const sectionErrors = useMemo(() => {
    const errors = form.formState.errors;
    return new Set(stepDefinitions.filter((step) => Boolean(errors[step.key])).map((step) => step.key));
  }, [form.formState.errors]);

  const toggleMultiSelect = (
    fieldName: "section1.componentTypes" | "section3.repairTypes" | "section5.defectTypes",
    value: string
  ) => {
    const nextValues = form.getValues(fieldName).includes(value)
      ? form.getValues(fieldName).filter((item) => item !== value)
      : [...form.getValues(fieldName), value];

    form.setValue(fieldName, nextValues, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const updateDesiredRepairLife = (value: string) => {
    form.setValue("section7.desiredRepairLife", value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    if (value !== "Other") {
      form.setValue("section7.desiredRepairLifeOther", "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  const applyConditionalSectionValidation = (sectionKey: SectionKey, values: RepairIntakeFormData) => {
    const conditionalFieldsBySection: Partial<Record<SectionKey, Path<RepairIntakeFormData>[]>> = {
      section1: ["section1.componentTypeOther"],
      section2: ["section2.deepestCorrosionDepth"],
      section3: ["section3.repairTypeOther"],
      section5: ["section5.defectTypeOther"],
      section6: ["section6.leakMitigationPlan", "section6.clientLeakStopPlan"],
      section7: ["section7.desiredRepairLifeOther", "section7.inchesOfGoodPipe"],
    };

    const conditionalFieldPaths = conditionalFieldsBySection[sectionKey] ?? [];
    if (conditionalFieldPaths.length) {
      form.clearErrors(conditionalFieldPaths);
    }

    let hasConditionalErrors = false;

    const setConditionalError = (fieldPath: Path<RepairIntakeFormData>, message = "This field is required") => {
      hasConditionalErrors = true;
      form.setError(fieldPath, {
        type: "manual",
        message,
      });
    };

    if (sectionKey === "section1" && values.section1.componentTypes.includes("Other")) {
      if (!values.section1.componentTypeOther?.trim()) {
        setConditionalError("section1.componentTypeOther");
      }
    }

    if (sectionKey === "section2" && values.section2.utReadingsAttached === "No") {
      if (!values.section2.deepestCorrosionDepth.trim()) {
        setConditionalError("section2.deepestCorrosionDepth");
      }
    }

    if (sectionKey === "section3" && values.section3.repairTypes.includes("Other")) {
      if (!values.section3.repairTypeOther?.trim()) {
        setConditionalError("section3.repairTypeOther");
      }
    }

    if (sectionKey === "section5" && values.section5.defectTypes.includes("Other")) {
      if (!values.section5.defectTypeOther?.trim()) {
        setConditionalError("section5.defectTypeOther");
      }
    }

    if (sectionKey === "section6" && values.section6.activeLeak === "Yes") {
      if (!values.section6.leakMitigationPlan.trim()) {
        setConditionalError("section6.leakMitigationPlan");
      }
      if (!values.section6.clientLeakStopPlan.trim()) {
        setConditionalError("section6.clientLeakStopPlan");
      }
    }

    if (sectionKey === "section7") {
      if (values.section7.desiredRepairLife === "Other" && !values.section7.desiredRepairLifeOther?.trim()) {
        setConditionalError("section7.desiredRepairLifeOther");
      }
      if (values.section7.goodPipeOnLandingZones === "No" && !values.section7.inchesOfGoodPipe.trim()) {
        setConditionalError("section7.inchesOfGoodPipe");
      }
    }

    return !hasConditionalErrors;
  };

  const resetToInitialState = () => {
    resetForm();
    form.reset(useRepairIntakeStore.getState().formData);
    setActiveStep(0);
  };

  const goToStep = async (nextStep: number) => {
    if (nextStep <= activeStep) {
      setActiveStep(nextStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const isCurrentStepValid = await form.trigger(currentStep.key);
    const normalizedValues = withComponentLabels(form.getValues());
    const isConditionalValidationValid = applyConditionalSectionValidation(currentStep.key, normalizedValues);

    if (!isCurrentStepValid || !isConditionalValidationValid) {
      return;
    }

    setSection(getStepNumber(currentStep.key), normalizedValues[currentStep.key]);
    setActiveStep(nextStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddAttachment = (payload: { file: File }) => {
    const currentFiles = form.getValues("section9.attachments");
    if (currentFiles.length >= 10) {
      toast({
        variant: "destructive",
        title: "Attachment limit reached",
        description: "Upload up to 10 supported files. Max 100 MB per file.",
      });
      return;
    }

    form.setValue("section9.attachments", [...currentFiles, payload.file], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleRemoveAttachment = (index: number) => {
    const currentFiles = form.getValues("section9.attachments");
    form.setValue(
      "section9.attachments",
      currentFiles.filter((_, currentIndex) => currentIndex !== index),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    );
  };

  const handleSubmitForm = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      const parsed = repairIntakeSchema.safeParse(form.getValues());
      if (!parsed.success) {
        const invalidSection = parsed.error.issues[0]?.path[0];
        const invalidIndex = stepDefinitions.findIndex((step) => step.key === invalidSection);
        if (invalidIndex >= 0) {
          setActiveStep(invalidIndex);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
      return;
    }

    const normalizedValues = withComponentLabels(form.getValues());
    console.log("Repair intake form submitted", normalizedValues);

    stepDefinitions.forEach((step) => {
      setSection(getStepNumber(step.key), normalizedValues[step.key]);
    });
    submitForm();
    setShowSuccessState(true);
  };

  if (submitted && showSuccessState) {
    return (
      <RepairIntakeSuccessState
        onStartNewForm={() => {
          resetToInitialState();
          setShowSuccessState(false);
        }}
        onReviewSubmission={() => {
          setActiveStep(0);
          setShowSuccessState(false);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <RepairIntakeStepProgress activeStep={activeStep} totalSteps={stepDefinitions.length} />

        <RepairIntakeStepNavigation
          activeStep={activeStep}
          sectionErrors={sectionErrors}
          steps={stepDefinitions}
          onGoToStep={(stepIndex) => {
            void goToStep(stepIndex);
          }}
        />

        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="space-y-2 border-gray-200 border-b">
            <CardTitle>{currentStep.title}</CardTitle>
            <CardDescription>Required fields must be completed before moving to the next step.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <Form {...form}>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                }}
                className="space-y-8"
              >
                <RepairIntakeStepFields
                  currentStepKey={currentStep.key}
                  form={form}
                  componentTypes={componentTypes}
                  repairTypes={repairTypes}
                  defectTypes={defectTypes}
                  utReadingsAttached={utReadingsAttached}
                  activeLeak={activeLeak}
                  desiredRepairLife={desiredRepairLife}
                  goodPipeOnLandingZones={goodPipeOnLandingZones}
                  attachments={attachments}
                  componentEntryIds={componentEntriesArray.fields.map((field) => field.id)}
                  componentEntryCount={componentEntriesArray.fields.length}
                  defectLengthFields={defectLengthArray.fields}
                  desiredRepairLengthFields={desiredRepairLengthArray.fields}
                  onToggleMultiSelect={toggleMultiSelect}
                  onUpdateDesiredRepairLife={updateDesiredRepairLife}
                  onAddComponent={() => componentEntriesArray.append(createEmptyComponentEntry())}
                  onRemoveComponent={(index) => componentEntriesArray.remove(index)}
                  onAddAttachment={handleAddAttachment}
                  onRemoveAttachment={handleRemoveAttachment}
                />

                <RepairIntakeFormActions
                  activeStep={activeStep}
                  isLastStep={activeStep === stepDefinitions.length - 1}
                  onBack={() => {
                    void goToStep(activeStep - 1);
                  }}
                  onReset={resetToInitialState}
                  onNext={() => {
                    void goToStep(activeStep + 1);
                  }}
                  onSubmit={() => {
                    void handleSubmitForm();
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default RepairIntakeForm;
